import { useEffect } from 'react';

const CANONICAL_ORIGIN = 'https://www.petito.be';

export type PageMetaOptions = {
    /** Full page title (already includes brand if needed). When set, document.title is updated. */
    title?: string | null;
    description?: string | null;
    /** Path only, e.g. /products/12 — becomes absolute www URL. */
    path?: string | null;
    imageUrl?: string | null;
    /** When true, sets noindex,nofollow. */
    noIndex?: boolean;
    /** Optional JSON-LD object (Product etc.). Cleared on unmount. */
    jsonLd?: Record<string, unknown> | null;
};

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
    const selector = attr === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
    let el = document.head.querySelector(selector) as HTMLMetaElement | null;
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.content = content;
}

function upsertLink(rel: string, href: string) {
    let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
    if (!el) {
        el = document.createElement('link');
        el.rel = rel;
        document.head.appendChild(el);
    }
    el.href = href;
}

function upsertJsonLd(data: Record<string, unknown> | null) {
    const id = 'petito-jsonld';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!data) {
        el?.remove();
        return;
    }
    if (!el) {
        el = document.createElement('script');
        el.type = 'application/ld+json';
        el.id = id;
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

/**
 * Lightweight client SEO: description, OG, robots, canonical, optional JSON-LD.
 * Complements useDocumentTitle for tab titles.
 */
export function usePageMeta(opts: PageMetaOptions) {
    const { title, description, path, imageUrl, noIndex, jsonLd } = opts;

    useEffect(() => {
        if (title != null && title.trim()) {
            document.title = title.trim();
        }

        if (description != null && description.trim()) {
            const d = description.trim().slice(0, 300);
            upsertMeta('name', 'description', d);
            upsertMeta('property', 'og:description', d);
            upsertMeta('name', 'twitter:description', d);
        }

        if (title != null && title.trim()) {
            upsertMeta('property', 'og:title', title.trim());
            upsertMeta('name', 'twitter:title', title.trim());
        }

        if (path != null) {
            const p = path.startsWith('/') ? path : `/${path}`;
            const url = p === '/' ? `${CANONICAL_ORIGIN}/` : `${CANONICAL_ORIGIN}${p}`;
            upsertLink('canonical', url);
            upsertMeta('property', 'og:url', url);
        }

        if (imageUrl != null && imageUrl.trim()) {
            const img = imageUrl.trim();
            upsertMeta('property', 'og:image', img);
            upsertMeta('name', 'twitter:image', img);
        }

        upsertMeta(
            'name',
            'robots',
            noIndex ? 'noindex, nofollow' : 'index, follow'
        );

        upsertJsonLd(jsonLd ?? null);

        return () => {
            // Restore default robots when leaving noindex routes
            if (noIndex) {
                upsertMeta('name', 'robots', 'index, follow');
            }
            upsertJsonLd(null);
        };
    }, [title, description, path, imageUrl, noIndex, jsonLd]);
}

export { CANONICAL_ORIGIN };
